import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { CreditCard, CheckCircle, XCircle, Loader2, Laptop, User, Wrench, Shield } from 'lucide-react'

const API_BASE = '/api'

const REPAIR_TYPE_LABELS: Record<string, string> = {
  screen: 'Pantalla', keyboard: 'Teclado', motherboard: 'Placa base',
  battery: 'Batería', power: 'Carga / Jack de alimentación', hinge: 'Bisagras',
  casing: 'Carcasa', general: 'Reparación general', console: 'Consola',
}

export default function RepairPaymentPage() {
  const { code, token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [formData, setFormData] = useState<any>(null)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (!code || !token) {
      setError('Enlace de pago no válido')
      setLoading(false)
      return
    }
    loadPaymentData()
  }, [code, token])

  const loadPaymentData = async () => {
    try {
      const res = await fetch(`${API_BASE}/repairs/pay/${code}/${token}`)
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.detail || 'Error al cargar datos de pago')
      }
      const data = await res.json()
      if (!data.success) {
        throw new Error(data.error || 'Error al generar formulario de pago')
      }
      setFormData(data)
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos de pago')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submit()
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-slate-600">Cargando datos de pago...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Error</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    )
  }

  const repair = formData?.repair
  const amount = repair?.amount ? repair.amount.toFixed(2) : ''
  const device = [repair?.device_brand, repair?.device_model].filter(Boolean).join(' ')
  const repairTypeLabel = REPAIR_TYPE_LABELS[repair?.repair_type] || repair?.repair_type || ''

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-5 text-center">
          <img src="/jvs-logo.jpg" alt="JVS Informática" className="h-10 mx-auto mb-3 rounded" />
          <h1 className="text-lg font-bold text-white">Pago de reparación</h1>
          <p className="text-indigo-200 text-sm mt-0.5">{code}</p>
        </div>

        <div className="p-6">
          {/* Repair details card */}
          {repair && (
            <div className="bg-slate-50 rounded-xl p-4 mb-5 space-y-2.5">
              {repair.customer_name && (
                <div className="flex items-center gap-2.5">
                  <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-700 font-medium">{repair.customer_name}</span>
                </div>
              )}
              {device && (
                <div className="flex items-center gap-2.5">
                  <Laptop className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{device}</span>
                </div>
              )}
              {repairTypeLabel && (
                <div className="flex items-center gap-2.5">
                  <Wrench className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{repairTypeLabel}</span>
                </div>
              )}
            </div>
          )}

          {/* Amount */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-5 text-center">
            <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider mb-1">Importe a pagar</p>
            <p className="text-4xl font-extrabold text-slate-900">{amount} <span className="text-lg font-bold text-slate-500">€</span></p>
            <p className="text-xs text-slate-500 mt-1">IVA incluido</p>
          </div>

          {/* Pay button */}
          {formData && (
            <form ref={formRef} action={formData.redsys_url} method="POST">
              <input type="hidden" name="Ds_SignatureVersion" value={formData.Ds_SignatureVersion} />
              <input type="hidden" name="Ds_MerchantParameters" value={formData.Ds_MerchantParameters} />
              <input type="hidden" name="Ds_Signature" value={formData.Ds_Signature} />
              <button type="button" onClick={handleSubmit}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 shadow-md shadow-emerald-200">
                <CreditCard className="w-5 h-5" />
                Pagar con tarjeta
              </button>
            </form>
          )}

          {/* Security badge */}
          <div className="flex items-center justify-center gap-2 mt-4 py-3 border-t border-slate-100">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-400">Pago seguro procesado por</span>
            <img src="https://sis.redsys.es/sis/img/redsys.png" alt="Redsys" className="h-4" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
          </div>
        </div>
      </div>
    </div>
  )
}

export function RepairPaymentOkPage() {
  const { code } = useParams()
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Pago realizado</h1>
        <p className="text-slate-600 mb-4">
          El pago de tu reparación <strong>{code}</strong> se ha procesado correctamente.
        </p>
        <p className="text-sm text-slate-500">
          Recibirás un email de confirmación. Procederemos a enviarte tu dispositivo reparado.
        </p>
        <a href={`/repairs/track/${code}`}
          className="inline-block mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-6 rounded-xl transition-colors">
          Ver estado de mi reparación
        </a>
      </div>
    </div>
  )
}

export function RepairPaymentKoPage() {
  const { code } = useParams()
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Pago no completado</h1>
        <p className="text-slate-600 mb-4">
          El pago de tu reparación <strong>{code}</strong> no se ha podido procesar.
        </p>
        <p className="text-sm text-slate-500 mb-6">
          Puedes intentarlo de nuevo o contactarnos si necesitas ayuda.
        </p>
        <a href={`/repairs/track/${code}`}
          className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2.5 px-6 rounded-xl transition-colors">
          Volver a mi reparación
        </a>
      </div>
    </div>
  )
}
